<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const settings = reactive({
  prompt_cache_enabled: true,
  general_model_knowledge_enabled: false,
  general_model_knowledge_risk_confirmed: false,
});

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await cortexSettingsAPI.get();
    Object.assign(settings, data.model || {});
  } catch (error) {
    useAlert(t('CORTEX_MODELS.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    const { data } = await cortexSettingsAPI.update({ model: settings });
    Object.assign(settings, data.model || {});
    useAlert(t('CORTEX_MODELS.BEHAVIOR.SAVED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.SAVE')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadSettings);
</script>

<template>
  <div class="flex flex-col gap-6" :class="{ 'opacity-60': isLoading }">
    <section class="pb-6 border-b border-n-weak">
      <h4 class="text-heading-3 text-n-slate-12">
        {{ t('CORTEX_MODELS.BEHAVIOR.CACHE_TITLE') }}
      </h4>
      <p class="mt-1 text-body-small text-n-slate-11">
        {{ t('CORTEX_MODELS.BEHAVIOR.CACHE_SECTION_DESCRIPTION') }}
      </p>
      <label class="flex items-start gap-3 mt-4 cursor-pointer">
        <NextSwitch v-model="settings.prompt_cache_enabled" class="mt-0.5" />
        <span class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('CORTEX_MODELS.BEHAVIOR.CACHE_LABEL') }}
          </span>
          <span class="block mt-1 text-body-small text-n-slate-11">
            {{ t('CORTEX_MODELS.BEHAVIOR.CACHE_DESCRIPTION') }}
          </span>
        </span>
      </label>
    </section>

    <section>
      <h4 class="text-heading-3 text-n-slate-12">
        {{ t('CORTEX_MODELS.BEHAVIOR.KNOWLEDGE_TITLE') }}
      </h4>
      <p class="mt-1 text-body-small text-n-slate-11">
        {{ t('CORTEX_MODELS.BEHAVIOR.KNOWLEDGE_SECTION_DESCRIPTION') }}
      </p>
      <label class="flex items-start gap-3 mt-4 cursor-pointer">
        <NextSwitch
          v-model="settings.general_model_knowledge_enabled"
          class="mt-0.5"
        />
        <span class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('CORTEX_MODELS.BEHAVIOR.KNOWLEDGE_LABEL') }}
          </span>
          <span class="block mt-1 text-body-small text-n-slate-11">
            {{ t('CORTEX_MODELS.BEHAVIOR.KNOWLEDGE_DESCRIPTION') }}
          </span>
        </span>
      </label>
      <label
        v-if="settings.general_model_knowledge_enabled"
        class="flex items-start gap-3 p-4 mt-4 rounded-lg bg-n-alpha-2 cursor-pointer"
      >
        <NextSwitch
          v-model="settings.general_model_knowledge_risk_confirmed"
          class="mt-0.5"
        />
        <span class="text-body-small text-n-slate-11">
          {{ t('CORTEX_MODELS.BEHAVIOR.KNOWLEDGE_CONFIRMATION') }}
        </span>
      </label>
    </section>

    <div class="flex justify-end pt-2">
      <NextButton
        :label="t('CORTEX_MODELS.ACTIONS.SAVE_BEHAVIOR')"
        icon="i-lucide-save"
        :is-loading="isSaving"
        :disabled="isLoading"
        @click="saveSettings"
      />
    </div>
  </div>
</template>
